import type { TypedStartListening } from '@reduxjs/toolkit'
import { createListenerMiddleware } from '@reduxjs/toolkit'
import { API_CALL_SUCCESS } from '../actions/actionConstants'
import { CHECKOUT_ACCOMMODATION_V2_ENQUIRY, FETCH_FINAL_FLIGHT_FARES, FETCH_MERCHANDISE_SHIPPING_QUOTES } from '../actions/apiActionConstants'
import { addPromoCode, refetchCommissionPromoCode, revalidatePromoCode } from '../actions/CheckoutActions'
import { hasAccommodationV2PromoInputsChanged } from '../lib/promo/promoMappers'
import type { AppDispatch } from '../store'

function isFetchFinalFlightFaresSuccess(action: unknown): action is {
  type: string
  api: string
} {
  return !!action &&
    typeof action === 'object' &&
    'api' in action &&
    'type' in action &&
    action.api === FETCH_FINAL_FLIGHT_FARES &&
    action.type === API_CALL_SUCCESS
}

function isFetchMerchandiseShippingQuotesSuccess(action: unknown): action is {
  type: string
  api: string
} {
  return !!action &&
    typeof action === 'object' &&
    'api' in action &&
    'type' in action &&
    action.api === FETCH_MERCHANDISE_SHIPPING_QUOTES &&
    action.type === API_CALL_SUCCESS
}

function isAccommodationV2EnquirySuccess(action: unknown): action is {
  type: string
  api: string
} {
  return !!action &&
    typeof action === 'object' &&
    'api' in action &&
    'type' in action &&
    action.api === CHECKOUT_ACCOMMODATION_V2_ENQUIRY &&
    action.type === API_CALL_SUCCESS
}

/**
 * Middleware that re-applies promo codes after flight seat selection updates
 * the cart totals via FETCH_FINAL_FLIGHT_FARES success action, after
 * merchandise shipping quotes load (which can introduce delivery fee items),
 * or after an accommodationV2 checkout enquiry moves what a room is priced on.
 */
const promoReapplicationMiddleware = createListenerMiddleware()

type AppStartListening = TypedStartListening<App.State, AppDispatch>
const startAppListening = promoReapplicationMiddleware.startListening as AppStartListening

startAppListening({
  predicate: (action) =>
    isFetchFinalFlightFaresSuccess(action) || isFetchMerchandiseShippingQuotesSuccess(action),
  effect: async(action, listenerApi) => {
    // Re-apply promo codes after the Redux state has been updated with the new seat prices
    const state = listenerApi.getState()
    const existingPromoCode = state.checkout.payment.promoCode

    if (!state.checkout.isFetchingPromo && existingPromoCode) {
      listenerApi.dispatch(addPromoCode(existingPromoCode))
    }

    // Also re-apply commission promo codes for agent hub
    listenerApi.dispatch(refetchCommissionPromoCode())
  },
})

// Checkout enquiry re-runs on entry, on refresh, after a price change is accepted and before purchase,
// and any run can move a room's price or, after `fallback_selected`, its supplier. The payment page only
// re-validates on a subtotal change, which a supplier move need not cause, and the pre-purchase enquiry
// builds the order straight away. Comparing the discount inputs keeps enquiries that move nothing from
// each sending a discount request.
startAppListening({
  predicate: (action, currentState, previousState) =>
    isAccommodationV2EnquirySuccess(action) && hasAccommodationV2PromoInputsChanged(previousState, currentState),
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(revalidatePromoCode())
  },
})

export default promoReapplicationMiddleware
